import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app'
import ContactDto from '@/models/Contacts/ContactDto';
import ContactLocationDto from '@/models/ContactLocations/ContactLocationDto';
import NoteDto from '@/models/Notes/NoteDto';
import UpdateNoteRequest from '@/models/Notes/UpdateNoteRequest';
import CreateNoteRequest from '@/models/Notes/CreateNoteRequest';
import LocationDto from '@/models/Locations/LocationDto';
import CreateContactLocationRequest from '@/models/ContactLocations/CreateContactLocationRequest';
import UpdateContactRequest from '@/models/Contacts/UpdateContactRequest';
import UpdateContactStatusRequest from '@/models/Contacts/UpdateContactStatusRequest';
import ContactWorkerDto from '@/models/ContactWorkers/ContactWorkerDto';
import WorkerDto from '@/models/Workers/WorkerDto';
import NoteCategoryDto from '@/models/NoteCategories/NoteCategoryDto';


export interface IContactDetailsState {
  loading: boolean; 
  currentContact: ContactDto;
  contactLocations: ContactLocationDto[];
  companyLocations: LocationDto[];
  loadingContactLocations: boolean;
  loadingCompanyLocations: boolean;
  notes: NoteDto[];
  noteCategories: NoteCategoryDto[];
  loadingNotes: boolean;
  workers: ContactWorkerDto[];
  loadingWorkers: boolean;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'ContactDetails', namespaced: true })
class ContactDetails extends VuexModule implements IContactDetailsState {
 
  public loading = true;
  public currentContact: ContactDto = new ContactDto();
  public contactLocations: ContactLocationDto[] = [];
  public companyLocations: LocationDto[] = [];
  public loadingContactLocations = true;
  public loadingCompanyLocations = true;
  public notes: NoteDto[] = [];
  public noteCategories: NoteCategoryDto[] = [];
  public loadingNotes = true;
  public workers: ContactWorkerDto[] = [];
  public loadingWorkers = true;
  public loadingDialog = false;
  
  @Action
  public async getContact(contact: ContactDto) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/contacts/${contact.id}`)
    .then(response => {
      this.context.commit("ContactDetails/setCurrentContact", response.data, { root: true });
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async editContact(request: UpdateContactRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/contacts/${this.currentContact.id}`, request)
    .then(response => {
      this.context.commit("ContactDetails/setCurrentContact", response.data, { root: true });
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editStatus(contact: ContactDto) {
    this.setLoadingDialog(true);
    const request = new UpdateContactStatusRequest();
    request.updatedAt = contact.updatedAt;
    request.status = contact.status;

    await ajax.put(`/api/v1/agency/contacts/${this.currentContact.id}/status`, request)
    .then(response => {
      this.context.commit("setCurrentContact", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getContactLocations() {
    this.setLoadingContactLocations(true);
    //Can't use currentCompany.id here yet because the request might have come from the Agency Booking List
    await ajax.get(`/api/v1/agency/contacts/${this.currentContact.id}/locations`)
    .then(response => {
      this.context.commit("setContactLocations", response.data);
      this.setLoadingContactLocations(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingContactLocations(false);
    });
  }

  @Action
  async getCompanyLocations() {
    this.setLoadingCompanyLocations(true);
    //Can't use currentCompany.id here yet because the request might have come from the Agency Booking List
    await ajax.get(`/api/v1/agency/companies/${this.currentContact.companyId}/locations`)
    .then(response => {
      this.context.commit("setCompanyLocations", response.data);
      this.setLoadingCompanyLocations(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingCompanyLocations(false);
    });
  }

  @Action
  public async createContactLocation(request: CreateContactLocationRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/contacts/${this.currentContact.id}/locations`, request)
    .then(response => {
      this.context.commit("setContactLocationFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async deleteContactLocation(contactLocationId: string) {
    this.setLoadingDialog(true); 
    await ajax.delete(`/api/v1/agency/contacts/${this.currentContact.id}/locations/${contactLocationId}`)
    .then(response => {
      this.context.commit("setContactLocationFromDelete", contactLocationId);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getNoteCategories() {
    this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/note-categories?entityType=6`)
    .then(response => {
      this.context.commit("setNoteCategories", response.data);
      //this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoadingNotes(false);
    });
  }

  @Action
  async getNotes() {
    this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/contacts/${this.currentContact.id}/notes`)
    .then(response => {
      this.context.commit("setNotes", response.data);
      this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingNotes(false);
    });
  }

  @Action
  async createNote(request: CreateNoteRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/contacts/${this.currentContact.id}/notes`, request)
    .then(response => {
      this.context.commit("setNoteFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editNote(note: NoteDto) {
    const request = new UpdateNoteRequest();
    request.updatedAt = note.updatedAt
    request.description = note.description;
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/contacts/${this.currentContact.id}/notes/${note.id}`, request)
    .then(response => {
      this.context.commit("setNoteFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getWorkers() {
    this.setLoadingWorkers(true);
    await ajax.get(`/api/v1/agency/contacts/${this.currentContact.id}/workers`)
    .then(response => {
      this.context.commit("setWorkers", response.data);
      this.setLoadingWorkers(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingWorkers(false);
    });
  }

  @Action
  async updateContactWorkerIsBlocking(payload: { id: string; isBlocking: boolean }) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/contact-workers/${payload.id}/is-blocking`, { isBlocking: payload.isBlocking })
    .then(() => {
      this.getWorkers();
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  cleanState() {
      this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.currentContact = new ContactDto();
    this.contactLocations = [];
    this.companyLocations = [];
    this.notes = [];
    this.noteCategories = [];
    this.workers = [];
    this.loading = true;
    this.loadingNotes = true;
    this.loadingContactLocations = true;
    this.loadingCompanyLocations = true;
    this.loadingDialog = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  }
  
  @Mutation
  private async setCurrentContact(contact: ContactDto) {
      this.currentContact = contact;
  }

  @Mutation
  private async setLoadingContactLocations(isLoading: boolean) {
      this.loadingContactLocations = isLoading;
  }

  @Mutation
  private async setLoadingCompanyLocations(isLoading: boolean) {
      this.loadingCompanyLocations = isLoading;
  }

  @Mutation
  private async setContactLocations(locations: ContactLocationDto[]) {
      this.contactLocations = locations;
  }

  @Mutation
  private async setCompanyLocations(locations: LocationDto[]) {
    this.companyLocations = [];
    const existingContactLocations = this.contactLocations.map(contactLocation => {
      return contactLocation.locationId;
    })
    for (let index = 0; index < locations.length; index++) {
      const location = locations[index];
      if (existingContactLocations.indexOf(location.id) == -1) {
        this.companyLocations.push(location);
      }
    }
  }

  @Mutation
  private async setContactLocationFromCreate(contactLocation: ContactLocationDto) {
      this.contactLocations.unshift(contactLocation);
  }

  @Mutation
  private async setContactLocationFromDelete(contactLocationId: string) {
    this.contactLocations.forEach( (item, index) => {
      if(item.id === contactLocationId) this.contactLocations.splice(index,1);
    });
  }

  @Mutation
  private async setNoteCategories(noteCategories: NoteCategoryDto[]) {
      this.noteCategories = noteCategories;
  }

  @Mutation
  private async setNotes(notes: NoteDto[]) {
      this.notes = notes;
  }

  @Mutation
  private async setNoteFromCreate(note: NoteDto) {
      this.notes.unshift(note);
  }

  @Mutation
  private async setNoteFromEdit(note: NoteDto) {
      const index = this.notes.findIndex((findNote: NoteDto) => findNote.id === note.id)
      this.notes.splice(index, 1, note);
  }

  @Mutation
  private async setLoadingNotes(isLoading: boolean) {
      this.loadingNotes = isLoading;
  }

  @Mutation
  private async setWorkers(workers: ContactWorkerDto[]) {
      this.workers = workers;
  }

  @Mutation
  private async setLoadingWorkers(isLoading: boolean) {
      this.loadingWorkers = isLoading;
  }
}

export const ContactDetailsModule = getModule(ContactDetails)
